import { DenominationInput, Text, Button, TextField, PopOverMenu, Pill } from "components/commons";
import { formatAmount, dateTimeFormat, formatVolume, formatDecimal } from "utils";
import { prettifyPaymentMethod } from "utils/pretty.utils";
import Big from "big.js";
import styles from "./shift-records-details-cash-and-non-cash.module.scss";
import locale from "localization";
import { formatPumpNumber } from "utils/text.utils";
import { PumpMethodEnum, FormMode } from "enums";
import { Link } from "react-router-dom";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import TooltipForm from "components/commons/tooltip-form/tooltip-form.module.js";
import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useForm, useApi, useAlert, useModal } from "hooks";
import { updateShiftById } from "apis/shift-records.api";
import { SuccessModal } from "components/modals";
import { StocqClockIn } from "images";

const EditableReferenceNumber = ({
  refNumber,
  originalRefNumber,
  initialState,
  transactionId,
  transactionPaymentMethodId,
  onRefetch,
  shiftId,
  isView,
  allPaymentMethodRows = [],
  transactionNumber,
  updatedValue,
  onUpdateRefNumber,
  rowKey,
  editingRefId,
  setEditingRefId,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showChangeInfo, setShowChangeInfo] = useState(false);
  const changeInfoRef = useRef(null);
  const inputWrapperRef = useRef(null);
  const { addAlert } = useAlert();
  const successModal = useModal();

  const isEditing = !isView && editingRefId === rowKey;

  useEffect(() => {
    if (!showChangeInfo) return undefined;

    const handleClickOutside = (event) => {
      if (changeInfoRef.current && !changeInfoRef.current.contains(event.target)) {
        setShowChangeInfo(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showChangeInfo]);

  // Use savedRefNumber if available, otherwise use prop
  const displayRefNumber = updatedValue !== undefined ? updatedValue : refNumber;
  const hasRefNumberChanged =
    updatedValue !== undefined ||
    (originalRefNumber !== undefined && (originalRefNumber || "") !== (displayRefNumber || ""));
  const initialFormState = useMemo(
    () => initialState(displayRefNumber),
    [displayRefNumber, initialState],
  );
  const updateShift = useApi({
    api: updateShiftById,
    handleOwnError: true,
    params: {
      shiftId,
    },
  });

  const { fields, modifyField, isFormSubmittable, getFormValues, clearForm } = useForm({
    initialState: initialFormState,
  });

  const matchingRefCount = useMemo(() => {
    if (!transactionPaymentMethodId) return 0;
    const count = allPaymentMethodRows.filter(
      (row) => row.transactionPaymentMethodId === transactionPaymentMethodId,
    ).length;
    return count > 1 ? count - 1 : 0;
  }, [allPaymentMethodRows, transactionPaymentMethodId]);

  const closeEditor = useCallback(() => {
    setEditingRefId((current) => (current === rowKey ? null : current));
  }, [setEditingRefId, rowKey]);

  useEffect(() => {
    if (!isEditing) return undefined;

    const handleClickOutside = (event) => {
      if (showTooltip) return;
      if (inputWrapperRef.current?.contains(event.target)) return;
      closeEditor();
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isEditing, showTooltip, closeEditor]);

  const wasEditingRef = useRef(false);
  useEffect(() => {
    if (isEditing) {
      wasEditingRef.current = true;
      return;
    }
    if (!wasEditingRef.current) return;
    wasEditingRef.current = false;
    setShowTooltip(false);
    clearForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const handleRefNumberClick = () => {
    if (isView) return;
    setEditingRefId(rowKey);
  };

  const handleRefNumberBlur = () => {
    const currentValue = fields?.refNumber?.value || "";
    const originalValue = displayRefNumber || "";
    // Only show tooltip if value has changed
    if (currentValue !== originalValue) {
      setShowTooltip(true);
    }
  };

  const handleCancel = () => {
    closeEditor();
  };

  const handleConfirm = async () => {
    try {
      const params = getFormValues();
      const payload = {
        paymentMethod: {
          refNumber: params?.refNumber || "",
          transactionPaymentMethodId,
        },
      };

      await updateShift.request(payload);

      // Save the new value locally so it persists without refetch
      onUpdateRefNumber(transactionPaymentMethodId, params?.refNumber || "");

      setShowTooltip(false);
      closeEditor();

      successModal.show();
    } catch (error) {
      setShowTooltip(false);
      closeEditor();

      return addAlert({
        title: locale.errorWhileUpdatingReferenceNumber,
        subtitle: error?.data?.message || error?.message || "Unknown error occurred",
        type: "error",
        interval: 10000,
      });
    }
  };

  return (
    <>
      {!isEditing ? (
        <div className={styles.refNumberCell}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              transform: "translateY(3px)",
            }}
          >
            <Text
              className={styles.content}
              onClick={handleRefNumberClick}
              style={{ cursor: isView ? "default" : "pointer", lineHeight: 1 }}
            >
              {displayRefNumber || "--"}
            </Text>
            {hasRefNumberChanged && (
              <span ref={changeInfoRef} style={{ display: "inline-flex" }}>
                <TooltipForm
                  content={
                    <div className={styles.tooltipContentInfo}>
                      <locale.Populate
                        text={locale.thisReferenceNumberHasChangedFrom}
                        items={[
                          <b key="from">{originalRefNumber || "--"}</b>,
                          <b key="to">{displayRefNumber || "--"}</b>,
                        ]}
                      />
                    </div>
                  }
                  visible={showChangeInfo}
                  placement="left"
                  bubbleClassName={styles.tooltipBubbleInfo}
                >
                  <InfoOutlinedIcon
                    titleAccess={locale.edited}
                    onClick={() => setShowChangeInfo((prev) => !prev)}
                    style={{ fontSize: 20, color: "#5541D7", cursor: "pointer", flexShrink: 0 }}
                  />
                </TooltipForm>
              </span>
            )}
          </div>
        </div>
      ) : (
        <div ref={inputWrapperRef} style={{ display: "inline-block" }}>
          <TooltipForm
            content={
              <div>
                <div className={styles.tooltipContent}>
                  <locale.Populate
                    text={locale.thisReferenceNumberAppearsInOtherRows}
                    items={[matchingRefCount]}
                  />
                  <br />
                  <br />
                  {locale.doYouWantToUpdateAllInstancesOfThisReferenceNumber}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                  <Button outline style={{ marginRight: 8 }} onClick={handleCancel}>
                    {locale.cancel}
                  </Button>
                  <Button primary disabled={!isFormSubmittable} onClick={handleConfirm}>
                    {locale.confirm}
                  </Button>
                </div>
              </div>
            }
            disabled={!showTooltip}
            visible={showTooltip}
            placement="left"
          >
            <TextField
              {...fields?.refNumber}
              onChange={modifyField}
              onBlur={handleRefNumberBlur}
              disabled={isView}
            />
          </TooltipForm>
        </div>
      )}
      <SuccessModal
        image={StocqClockIn}
        title={locale.updateReferenceNumber}
        content={locale.successMessageModalReferenceNumber}
        {...successModal}
        close={() => {
          successModal.close();
          // await onRefetch();
        }}
        primary={{
          text: locale.gotIt,
          onClick: () => {
            successModal.close();
            // await onRefetch();
          },
        }}
      />
    </>
  );
};

const AdjustedInfo = ({ adjustedAt }) => (
  <div className={styles.adjustedInfo}>
    <Pill className={styles.adjustedPill}>{locale.adjusted}</Pill>
    <Text className={styles.adjustedDate}>{dateTimeFormat(adjustedAt)}</Text>
  </div>
);

const TransactionNumberCell = ({ transactionNumber, adjustedAt }) => (
  <div className={styles.transactionNumberCell}>
    <Text className={styles.content}>{transactionNumber ?? "--"}</Text>
    {adjustedAt && <AdjustedInfo adjustedAt={adjustedAt} />}
  </div>
);

export const getCashAndNonCashTotals = ({ payment, transactionsItemDetails, isNonCash = true }) => {
  let {
    sku,
    discount,
    totalAmount,
    discountPremium,
    vat,
    amountPaid,
    transactionId,
    excessAmount,
    finalAmountPaid: storedFinalAmountPaid,
  } = payment;

  let transactionDetails = transactionsItemDetails.find(
    (t) => t.transactionId === transactionId && t.sku === sku,
  );

  if (!transactionDetails) {
    transactionDetails = transactionsItemDetails.find((t) => t.transactionId === transactionId);
  }

  const mAmount = totalAmount - excessAmount;
  const mActualDiscount = mAmount - amountPaid;
  let isDiscountCorrect = false;

  const roundedWith2DecimalPlaces = (value) => {
    return Math.round(value * 100) / 100;
  };

  if (roundedWith2DecimalPlaces(mActualDiscount) === roundedWith2DecimalPlaces(discount)) {
    isDiscountCorrect = true;
  } else {
    isDiscountCorrect = false;
  }

  const discountMagnitude = Math.abs(discount ?? 0);
  let mDiscountPremium = 0;
  if (discount === 0 && (discountPremium ?? 0) === 0) {
    mDiscountPremium = 0;
  } else if (vat === 0 && totalAmount < amountPaid) {
    if (isDiscountCorrect) {
      mDiscountPremium = -discountMagnitude;
    } else {
      mDiscountPremium = discountMagnitude !== 0 ? -discountMagnitude : -mActualDiscount;
    }
  } else {
    mDiscountPremium = isDiscountCorrect
      ? -discountMagnitude
      : discountMagnitude !== 0
        ? -discountMagnitude
        : -mActualDiscount;
  }

  const mFinalAmount = mAmount + mDiscountPremium;

  const finalTotalAmount = mAmount; // as "Amount" Column
  const totalDiscount = mDiscountPremium; // as "Discount/Premium" Column
  const finalAmountPaid =
    discount === 0 && (discountPremium ?? 0) === 0 && vat === 0 && storedFinalAmountPaid != null
      ? storedFinalAmountPaid
      : mFinalAmount;

  const amount = finalTotalAmount - (discount ?? 0) + (discountPremium ?? 0);
  const excessAmountPaid = amountPaid > amount ? amountPaid - amount : 0;
  const hasDiscount = Math.abs(discount ?? 0) >= 0.01 && Math.abs(discount ?? 0) !== 0.01;

  // distribute excess amount
  const share = (finalAmountPaid / transactionDetails?.transaction?.amount) * excessAmount;
  const displayExcessAmount =
    discount > 0 &&
    transactionDetails?.transaction?.excessAmount > 0 &&
    transactionDetails?.transaction?.excessAmount === excessAmount
      ? share
      : excessAmount;

  const txn = transactionDetails?.transaction;
  const txnHasDiscountMagnitude =
    Math.abs(txn?.totalDiscount ?? 0) > 0.005 || Math.abs(txn?.discount ?? 0) > 0.005;
  const paymentHasDiscountMagnitude =
    Math.abs(discount ?? 0) > 0.005 || Math.abs(discountPremium ?? 0) > 0.005;
  const txnHasRealSalesDiscount = Math.abs(txn?.salesDiscount ?? 0) > 0.005;
  const isPremium =
    (discountPremium ?? 0) > 0.005 ||
    (txn != null &&
      !txnHasRealSalesDiscount &&
      (txnHasDiscountMagnitude || paymentHasDiscountMagnitude));

  if (isPremium) {
    const premiumMagnitude =
      (discountPremium ?? 0) > 0.005 ? discountPremium : Math.abs(discount ?? 0);
    const storedFinal =
      storedFinalAmountPaid != null && storedFinalAmountPaid > 0.005 ? storedFinalAmountPaid : null;
    const finalAmt =
      storedFinal != null
        ? storedFinal
        : amountPaid != null && Math.abs(amountPaid - (mAmount + premiumMagnitude)) < 0.5
          ? amountPaid
          : mAmount + premiumMagnitude;
    const aGap = Math.abs(mAmount + premiumMagnitude - finalAmt);
    const bGap = Math.abs(mAmount - finalAmt);
    const isAmountAlreadyGross = aGap <= bGap;
    const grossAmount = isAmountAlreadyGross
      ? mAmount
      : Number(Big(mAmount).minus(premiumMagnitude).round(2));
    return {
      payment,
      finalAmountPaid: finalAmt,
      excessAmountPaid: 0,
      transaction: transactionDetails?.transaction,
      totalDiscount: premiumMagnitude,
      displayExcessAmount: excessAmount,
      hasDiscount: true,
      totalAmount: grossAmount,
      shiftId: transactionDetails?.shiftId,
      isPremium: true,
    };
  }

  return {
    payment,
    finalAmountPaid,
    excessAmountPaid,
    transaction: transactionDetails?.transaction,
    totalDiscount: Math.abs(totalDiscount),
    displayExcessAmount,
    hasDiscount,
    totalAmount: finalTotalAmount,
    shiftId: transactionDetails?.shiftId,
    isPremium: false,
  };
};
export const nonSaleTransactionMapper = ({ val, initialState }) => {
  const {
    pumpNumber,
    name,
    refNumber,
    quantity,
    totalAmount,
    refName,
    plateNumber,
    type,
    remarks,
    transactionNumber,
    from,
  } = val;

  const isDryStock = !["Diesel", "Gas 91", "Gas 95", "Gas 97"].includes(name);

  const isNegative = (value) => {
    const number = parseFloat(value);
    return !isNaN(number) && number < 0;
  };

  function validate(value) {
    const number = parseFloat(value);
    return Math.abs(number);
  }

  return {
    pump: (
      <>
        <Text className={styles.content}>{isDryStock ? "--" : (pumpNumber ?? "1")}</Text>
      </>
    ),
    product: (
      <>
        <Text className={styles.content}>{name || "--"}</Text>
      </>
    ),
    transactionType: (
      <>
        <Text className={styles.content}>{PumpMethodEnum[from] || "--"}</Text>
      </>
    ),
    referenceNo: (
      <>
        <Text className={styles.content}>{refNumber || "--"}</Text>
      </>
    ),
    quantity: (
      <>
        <Text className={styles.content}>
          {type === "fuel" ? formatVolume(validate(quantity) ?? 0) : validate(quantity)}
        </Text>
      </>
    ),
    amount: (
      <>
        <Text
          style={{ width: "60px" }}
          className={isNegative(totalAmount) ? styles.negative : styles.content}
        >
          {formatAmount(totalAmount)}
        </Text>
      </>
    ),
    transactionId: (
      <>
        <Text className={styles.content}>{transactionNumber ?? ""}</Text>
      </>
    ),
    refName: (
      <>
        <Text className={styles.content}>{refName || "--"}</Text>
        <Text className={styles.content}>{plateNumber || "--"}</Text>
      </>
    ),
    remarks: (
      <>
        <Text className={styles.content}>{remarks || "--"}</Text>
      </>
    ),
  };
};

export const nonCashTransactionMapper = ({
  val,
  index,
  transactionsItemDetails = [],
  allPaymentMethodRows = [],
  initialState,
  onRefetch,
  pageMode,
  updatedRefNumbers = {},
  setUpdatedRefNumbers,
  updatePaymentMethodRefNumber,
  editingRefId = null,
  setEditingRefId,
  onAdjustPaymentMethod,
}) => {
  const { transaction } = getCashAndNonCashTotals({
    payment: val,
    transactionsItemDetails,
  });
  let {
    quantity,
    type,
    name,
    method,
    transactionNumber,
    refNumber,
    originalRefNumber,
    pumpNumber,
    refName,
    plateNumber,
    remarks,
    transactionId,
    transactionPaymentMethodId,
    totalAmount,
    discount,
    vatAmount,
    excessAmount,
    finalAmount,
    adjustedAt,
  } = val;

  // Calculate the ratio: (amount of payment method) / (total amount of transaction)
  // const paymentRatio =
  //   transactionTotalAmount > 0 ? paymentMethodAmount / transactionTotalAmount : 0;

  // Helper function for rounding
  // const roundTo2Decimals = (value) => Math.round(value * 100) / 100;

  // // Discount/Premium - productDiscount is already the proportional discount value from the transaction
  // const calculatedDiscount = roundTo2Decimals(productDiscount || 0);

  // // Check if this is a Gift Card payment method
  // const isGiftCard = method === PaymentMethod.GiftCard;

  // let calculatedAmount;
  // let calculatedFinalAmountPaid;

  // if (isGiftCard) {
  //   // For Gift Cards: use direct paymentMethodAmount as Final Amount Paid
  //   // Amount = Final Amount Paid + Discount (the original amount before discount was applied)
  //   calculatedFinalAmountPaid = roundTo2Decimals(paymentMethodAmount || 0);
  //   calculatedAmount = roundTo2Decimals(calculatedFinalAmountPaid + calculatedDiscount);
  // } else {
  //   // For other payment methods (split payments, etc.): use ratio formula
  //   // Amount = (amount of payment method) / (total amount of transaction) * (amount of product)
  //   calculatedAmount = roundTo2Decimals(paymentRatio * (productAmount || 0));
  //   // Final Amount Paid = Amount - Discount
  //   calculatedFinalAmountPaid = roundTo2Decimals(calculatedAmount - calculatedDiscount);
  // }

  // const isFuel = type === "fuel";

  const hasDiscount = discount < 0;

  const shiftId = transactionsItemDetails[0]?.shiftId;

  let reference = refName;
  if (reference === "") {
    reference = "--";
  }

  let plateNum = plateNumber;
  if (plateNum === "") {
    plateNum = "--";
  }

  let quantityFormat =
    type === "fuel"
      ? formatVolume(parseFloat(quantity).toFixed(3 ?? 0))
      : formatDecimal(quantity, 2);

  const isView = pageMode === FormMode.View;

  // Use transactionPaymentMethodId directly from val (enriched data) or fallback to finding it
  let finalTransactionPaymentMethodId = transactionPaymentMethodId;
  if (!finalTransactionPaymentMethodId) {
    const paymentMethods = transaction?.paymentMethods || [];
    const paymentMethodRecord = paymentMethods.find((pm) => pm.refNumber === refNumber);
    finalTransactionPaymentMethodId = paymentMethodRecord?.transactionPaymentMethodId;
  }

  return {
    paymentMethod: (
      <>
        <EditableReferenceNumber
          refNumber={refNumber}
          originalRefNumber={originalRefNumber}
          initialState={initialState}
          transactionId={transactionId}
          transactionPaymentMethodId={finalTransactionPaymentMethodId}
          onRefetch={onRefetch}
          shiftId={shiftId}
          isView={isView}
          allPaymentMethodRows={allPaymentMethodRows}
          transactionNumber={transactionNumber}
          updatedValue={updatedRefNumbers[finalTransactionPaymentMethodId]}
          rowKey={`row-${index}`}
          editingRefId={editingRefId}
          setEditingRefId={setEditingRefId}
          onUpdateRefNumber={(id, value) => {
            if (!id) return;
            setUpdatedRefNumbers((prev) => ({
              ...prev,
              [id]: value,
            }));
            updatePaymentMethodRefNumber?.(id, value);
          }}
        />
      </>
    ),
    amount: (
      <>
        <Text className={styles.content}>{formatAmount(totalAmount)}</Text>
      </>
    ),
    discount: (
      <>
        <Text className={hasDiscount ? styles.negative : styles.content}>
          {`${hasDiscount ? "-" : ""}${formatAmount(Math.abs(discount ?? 0))}`}
        </Text>
      </>
    ),
    finalAmount: (
      <>
        <Text className={styles.content}>{formatAmount(finalAmount)}</Text>
        {!isNaN(excessAmount) && excessAmount >= 0.005 && (
          <Text className={styles.italic}>{`Excess: ${formatAmount(excessAmount)}`}</Text>
        )}
      </>
    ),
    transactionId: (
      <TransactionNumberCell transactionNumber={transactionNumber} adjustedAt={adjustedAt} />
    ),
    product: (
      <>
        <Text
          className={styles.content}
          style={{
            width: "220px",
            maxWidth: "220px",
            overflowWrap: "break-word",
            whiteSpace: "normal",
            wordBreak: "break-all",
          }}
        >
          {name ?? "--"}
        </Text>
      </>
    ),
    pumpNumber: (
      <>
        <Text className={styles.content}>
          {type === "fuel" ? formatPumpNumber(pumpNumber.split(",")[0]) : "--"}
        </Text>
      </>
    ),
    quantity: (
      <>
        <Text className={styles.content}>{quantityFormat}</Text>
      </>
    ),
    refName: (
      <>
        <Text className={styles.content}>{reference ?? "--"}</Text>
        <Text className={styles.content}>{plateNum ?? "--"}</Text>
      </>
    ),
    remarks: (
      <>
        <Text className={styles.content}>{remarks ?? (transaction?.remarks || "--")}</Text>
      </>
    ),
    vatExempt: (() => {
      const vatVal = vatAmount;
      return (
        <>
          <Text className={vatVal < 0 ? styles.negative : styles.content}>
            {vatVal < 0 ? `-${formatAmount(-vatVal)}` : formatAmount(vatVal)}
          </Text>
        </>
      );
    })(),
    actions:
      isView || !onAdjustPaymentMethod || !finalTransactionPaymentMethodId ? null : (
        <div className={styles.rowActionMenu}>
          <PopOverMenu
            options={[
              {
                content: locale.editTransaction,
                onClick: () =>
                  onAdjustPaymentMethod({
                    transactionPaymentMethodId: finalTransactionPaymentMethodId,
                    transactionId,
                    method,
                    refNumber: updatedRefNumbers[finalTransactionPaymentMethodId] ?? refNumber,
                    transactionNumber,
                  }),
              },
            ]}
          >
            <MoreVertIcon className={styles.rowActionIcon} />
          </PopOverMenu>
        </div>
      ),
  };
};

export const cashTransactionMapper = ({
  val,
  transactionsItemDetails = [],
  onViewCashTransaction,
}) => {
  let {
    type,
    name,
    pumpNumber,
    quantity,
    totalAmount,
    finalAmount,
    // amountPaid,
    discount,
    vatAmount,
    transactionCount,
    adjustmentCount,

    // Values from enriched data for formula calculation
    // productAmount,
    // productDiscount,
    // transactionTotalAmount,
    // paymentMethodAmount,
  } = val;

  // Calculate the ratio: (amount of payment method) / (total amount of transaction)
  // const paymentRatio =
  //   transactionTotalAmount > 0 ? paymentMethodAmount / transactionTotalAmount : 0;

  // Helper functions for rounding
  // const roundTo2Decimals = (value) => Math.round(value * 100) / 100;
  // const ceilTo2Decimals = (value) => Math.ceil(value * 100) / 100;

  // Amount = (amount of payment method) / (total amount of transaction) * (amount of product)
  // Use ceiling to round up
  // const calculatedAmount = ceilTo2Decimals(paymentRatio * (productAmount || 0));

  // Discount/Premium - productDiscount is already the proportional discount value from the transaction
  // const calculatedDiscount = roundTo2Decimals(productDiscount || 0);

  // Final Amount Paid = Amount - Discount
  // const calculatedFinalAmountPaid = roundTo2Decimals(calculatedAmount - calculatedDiscount);

  // const finalAmount = totalAmount - discount;

  // const hasDiscount = calculatedDiscount > 0;
  // const hasNegativeSign = calculatedDiscount < 0;
  const hasDiscount = discount < 0;
  // const totalDiscount = hasNegativeSign ? Math.abs(calculatedDiscount) : calculatedDiscount;
  let quantityFormat = type === "fuel" ? formatVolume(quantity ?? 0) : formatDecimal(quantity, 2);

  return {
    product: (
      <>
        <Text className={styles.content}>{name ?? "--"}</Text>
      </>
    ),
    pumpNumber: (
      <>
        <Text className={styles.content}>
          {type === "fuel" ? formatPumpNumber(pumpNumber.split(",")[0]) : "--"}
        </Text>
      </>
    ),
    transactionCount: (
      <div className={styles.transactionCountCell}>
        <Text className={styles.content}>{transactionCount ?? "--"}</Text>
        {adjustmentCount > 0 && (
          <Pill className={styles.adjustmentCountPill}>
            {locale.populate(locale.xAdjustments, [adjustmentCount])}
          </Pill>
        )}
      </div>
    ),
    quantity: (
      <>
        <Text className={styles.content}>{quantityFormat}</Text>
      </>
    ),
    amount: (
      <>
        <Text className={styles.content}>{formatAmount(totalAmount)}</Text>
      </>
    ),
    discount: (
      <>
        <Text className={hasDiscount ? styles.negative : styles.content}>
          {`${hasDiscount ? "-" : ""}${formatAmount(discount)}`}
        </Text>
      </>
    ),
    finalAmountPaid: (
      <>
        <Text className={styles.content}>{formatAmount(finalAmount)}</Text>
      </>
    ),
    vatExempt: (() => {
      const vatVal = vatAmount;
      return (
        <>
          <Text className={vatVal < 0 ? styles.negative : styles.content}>{`${
            vatVal < 0 ? "-" : ""
          }${formatAmount(vatVal)}`}</Text>
        </>
      );
    })(),
    actions: onViewCashTransaction ? (
      <Button style={{ textDecoration: "none" }} link onClick={() => onViewCashTransaction(val)}>
        <div className={styles.viewAction}>
          <span>{locale.view}</span>
          <NavigateNextIcon fontSize="small" />
        </div>
      </Button>
    ) : null,
  };
};

export const cashTransactionDetailMapper = ({ val, onAdjustPaymentMethod }) => {
  const {
    type,
    quantity,
    transactionId,
    transactionNumber,
    transactionPaymentMethodId,
    method,
    refNumber,
    totalAmount,
    discount,
    finalAmount,
    excessAmount,
    adjustedAt,
    vatAmount,
  } = val;

  const hasDiscount = discount < 0;

  return {
    transactionNumber: (
      <TransactionNumberCell transactionNumber={transactionNumber} adjustedAt={adjustedAt} />
    ),
    amount: <Text className={styles.content}>{formatAmount(totalAmount)}</Text>,
    discount: (
      <Text className={hasDiscount ? styles.negative : styles.content}>
        {`${hasDiscount ? "-" : ""}${formatAmount(Math.abs(discount ?? 0))}`}
      </Text>
    ),
    quantity: (
      <Text className={styles.content}>
        {type === "fuel"
          ? formatVolume(parseFloat(quantity).toFixed(3))
          : formatDecimal(quantity, 2)}
      </Text>
    ),
    vatExempt: (() => {
      const vatVal = vatAmount ?? 0;
      return (
        <Text className={vatVal < 0 ? styles.negative : styles.content}>
          {vatVal < 0 ? `-${formatAmount(-vatVal)}` : formatAmount(vatVal)}
        </Text>
      );
    })(),
    finalAmount: (
      <>
        <Text className={styles.content}>{formatAmount(finalAmount)}</Text>
        {!isNaN(excessAmount) && excessAmount >= 0.005 && (
          <Text className={styles.italic}>{`Excess: ${formatAmount(excessAmount)}`}</Text>
        )}
      </>
    ),
    actions:
      !onAdjustPaymentMethod || !transactionPaymentMethodId ? null : (
        <div className={styles.rowActionMenu}>
          <PopOverMenu
            options={[
              {
                content: locale.editTransaction,
                onClick: () =>
                  onAdjustPaymentMethod({
                    transactionPaymentMethodId,
                    transactionId,
                    method,
                    refNumber,
                    transactionNumber,
                  }),
              },
            ]}
          >
            <MoreVertIcon className={styles.rowActionIcon} />
          </PopOverMenu>
        </div>
      ),
  };
};

export const expensesMapper = ({ val, onClickViewSlip }) => {
  const { expenseId, type, syncedAt, amount, remarks, imageUrl } = val;
  return {
    expenseId: (
      <>
        <Text className={styles.content}>{expenseId ?? ""}</Text>
      </>
    ),
    type: (
      <>
        <Text className={styles.content}>{type ?? ""}</Text>
      </>
    ),
    dateAndTime: (
      <>
        <Text className={styles.content}>{dateTimeFormat(syncedAt ?? "")}</Text>
      </>
    ),
    amount: (
      <>
        <Text className={styles.content}>{formatAmount(amount ?? 0)}</Text>
      </>
    ),
    remarks: (
      <>
        <Text className={styles.content}>{remarks ?? ""}</Text>
      </>
    ),
    receiptPreview: (
      <>
        <Button
          style={{ textDecoration: "none", background: !imageUrl ? "none" : undefined }}
          link
          onClick={() => onClickViewSlip(imageUrl)}
          disabled={!imageUrl}
        >
          {imageUrl ? (
            locale.view
          ) : (
            <>
              <Text className={styles.blank}>{"--"}</Text>
            </>
          )}
        </Button>
      </>
    ),
  };
};

export const cashOnHandMapper = ({ val, i, onDenominationChange }) => {
  const { denomination, safeDrop, totalAmount } = val;

  return {
    denomination: (
      <>
        <Text className={styles.content}>
          {denomination === 1 ? locale.coins : formatAmount(denomination)}
        </Text>
      </>
    ),
    safeDrops: (
      <>
        <Text className={styles.content}>{safeDrop}</Text>
      </>
    ),
    endShift: (
      <>
        <DenominationInput
          value={safeDrop}
          onDecrementClick={() => {
            onDenominationChange(i, "dec");
          }}
          onIncrementClick={() => {
            onDenominationChange(i, "inc");
          }}
        />
      </>
    ),
    total: (
      <>
        <Text className={styles.content}>{Number(Big(safeDrop))}</Text>
      </>
    ),
    totalAmount: (
      <>
        <Text className={styles.content}>{formatAmount(totalAmount)}</Text>
      </>
    ),
  };
};

export const netCashSummaryMapper = ({ val }) => {
  const { label, value } = val;
  return {
    label: (
      <>
        <Text className={styles.label}>{label ?? "0"}</Text>
      </>
    ),
    value: (
      <>
        <Text className={styles.content}>{value ?? "0"}</Text>
      </>
    ),
  };
};

export const bankDepositMapper = ({ val, onClickViewSlip }) => {
  const { bankDepositId, depositedAt, amount, type, remarks, imageUrl } = val;
  return {
    depositId: (
      <>
        <Text className={styles.content}>{bankDepositId ?? ""}</Text>
      </>
    ),
    dateAndTime: (
      <>
        <Text className={styles.content}>{dateTimeFormat(depositedAt ?? "")}</Text>
      </>
    ),
    amount: (
      <>
        <Text className={styles.content}>{formatAmount(amount ?? 0)}</Text>
      </>
    ),
    depositType: (
      <>
        <Text className={styles.content}>{type ?? ""}</Text>
      </>
    ),
    remarks: (
      <>
        <Text className={styles.content}>{remarks ?? ""}</Text>
      </>
    ),
    slipPreview: (
      <>
        <Button
          style={{ textDecoration: "none", background: !imageUrl ? "none" : undefined }}
          link
          onClick={() => onClickViewSlip(imageUrl)}
          disabled={!imageUrl}
        >
          {imageUrl ? (
            locale.view
          ) : (
            <>
              <Text className={styles.content}>{"--"}</Text>
            </>
          )}
        </Button>
      </>
    ),
  };
};

export const keyAccountCollectionMapper = ({ val }) => {
  const {
    keyAccountCollectionId,
    dateTime,
    amount,
    siNumber,
    keyAccountName,
    paymentMethod,
    remarks,
  } = val;
  return {
    collectionId: (
      <>
        <Text className={styles.content}>{keyAccountCollectionId ?? ""}</Text>
      </>
    ),
    dateTime: (
      <>
        <Text className={styles.content}>{dateTimeFormat(dateTime ?? "")}</Text>
      </>
    ),
    amount: (
      <>
        <Text className={styles.content}>{formatAmount(amount ?? 0)}</Text>
      </>
    ),
    siNumSoa: (
      <>
        <Text className={styles.content}>{siNumber ?? ""}</Text>
      </>
    ),
    keyAccountName: (
      <>
        <Text className={styles.content}>{keyAccountName ?? ""}</Text>
      </>
    ),
    paymentMethod: (
      <>
        <Text className={styles.content}>{prettifyPaymentMethod(paymentMethod ?? "")}</Text>
      </>
    ),
    remarks: (
      <>
        <Text className={styles.content}>{remarks ?? "--"}</Text>
      </>
    ),
  };
};

export const promoDiscountMapper = ({ val, shiftId }) => {
  const isPumpDiscount = val?.type?.includes("Pump Discount");
  const promoName = isPumpDiscount ? val.type : val?.discount;

  const volume = isPumpDiscount
    ? formatVolume(val.totalQuantities ?? 0)
    : val.totalLiters > 0
      ? formatVolume(val.totalLiters)
      : "--";
  const quantity = !isPumpDiscount && val.totalQuantities > 0 ? val.totalQuantities : "--";
  const totalTransactions =
    val?.totalTransactions > 0 ? val.totalTransactions : val?.transactions?.length;

  return {
    promoName: promoName,
    transactionCount: totalTransactions,
    volume: volume,
    qty: quantity,
    actions: (
      <Link
        target="_blank"
        rel="noopener noreferrer"
        to={`/bir-report/discount-report?shiftId=${shiftId}&promoName=${encodeURIComponent(
          promoName ?? "",
        )}`}
        style={{
          textDecoration: "none",
        }}
      >
        <div
          style={{
            color: "#5349b7",
            fontWeight: "500",
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
            alignItems: "center",
          }}
        >
          <span>View</span>
          <NavigateNextIcon fontSize="small" />
        </div>
      </Link>
    ),
  };
};
